import React, { useState } from 'react';
import axios from '../../../../api/myAxios';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormHelperText,
  Alert,
  Fade,
  CircularProgress,
} from '@mui/material';
import { Link as LinkIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';

function DataUrlUpload({
  workspaceId,
  onSuccess,
  isEditMode = false,
  editingItem = null,
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [url, setUrl] = useState(editingItem?.sourceUrl || '');
  const [description, setDescription] = useState(
    editingItem?.description || ''
  );
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  // 🚀 REACT QUERY MUTATION POUR URL UPLOAD/UPDATE
  const uploadUrlMutation = useMutation({
    mutationFn: async ({ url, workspaceId, description, isEdit, itemId }) => {
      if (isEdit) {
        const response = await axios.put(`/api/data/url/${itemId}`, {
          url,
          description,
        });
        return response.data;
      } else {
        const response = await axios.post('/api/data/url', {
          url,
          workspaceId,
          description,
        });
        return response.data;
      }
    },
    onSuccess: (data) => {
      // 🔥 INVALIDATION AUTOMATIQUE DES QUERIES
      queryClient.invalidateQueries({
        queryKey: ['dataItems', workspaceId],
      });

      // Reset form
      setUrl('');
      setDescription('');
      setHasAttemptedSubmit(false);

      // Callback externe si fourni
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      console.error(t('error.console.errorUploadingUrl'), error);
    },
  });

  const validateUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch (err) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setHasAttemptedSubmit(true);

    if (!validateUrl(url)) {
      return; // L'erreur sera gérée par la validation
    }

    uploadUrlMutation.mutate({
      url,
      workspaceId,
      description,
      isEdit: isEditMode,
      itemId: editingItem?._id,
    });
  };

  // 🔥 VALIDATION DES ERREURS
  const getUrlError = () => {
    if (!hasAttemptedSubmit) return null;
    if (!url.trim()) return t('dataUrlUpload.errorNoUrl');
    if (!validateUrl(url)) return t('dataUrlUpload.errorInvalidUrl');
    return null;
  };

  const urlError = getUrlError();
  const isFormValid = url.trim() && !urlError && !uploadUrlMutation.isLoading;

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {isEditMode ? t('dataUrlUpload.editTitle') : t('dataUrlUpload.title')}
        </Typography>
      </Box>

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label={t('dataUrlUpload.url')}
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder={t('dataUrlUpload.urlPlaceholder')}
          disabled={uploadUrlMutation.isLoading}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label={t('dataUrlUpload.description')}
          multiline
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('dataUrlUpload.descriptionPlaceholder')}
          disabled={uploadUrlMutation.isLoading}
          sx={{ mb: 2 }}
        />

        {/* 🔥 GESTION D'ERREURS AVEC REACT QUERY */}
        {urlError && (
          <FormHelperText error sx={{ mb: 2 }}>
            {urlError}
          </FormHelperText>
        )}

        {uploadUrlMutation.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {uploadUrlMutation.error?.response?.data?.message ||
              t('dataUrlUpload.errorUploading')}
          </Alert>
        )}

        {uploadUrlMutation.isSuccess && (
          <Fade in={uploadUrlMutation.isSuccess} timeout={1000}>
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('dataUrlUpload.uploadSuccess')}
            </Alert>
          </Fade>
        )}

        <Button
          type="submit"
          variant="contained"
          disabled={!isFormValid}
          fullWidth
          startIcon={
            uploadUrlMutation.isLoading ? <CircularProgress size={20} /> : null
          }
        >
          {uploadUrlMutation.isLoading
            ? isEditMode
              ? t('dataUrlUpload.updating')
              : t('dataUrlUpload.uploading')
            : isEditMode
              ? t('dataUrlUpload.updateUrl')
              : t('dataUrlUpload.uploadUrl')}
        </Button>
      </Box>
    </Paper>
  );
}

export default DataUrlUpload;
